import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { EntitySelect } from '@/components/EntitySelect';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { EXPENSE_CATEGORIES, RECURRING_FREQUENCIES } from '../constants';
import type { RecurringExpenseInput } from '../types';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD'] as const;

const schema = z
  .object({
    scope: z.enum(['company', 'employee']),
    employee: z.string(),
    description: z.string().trim().min(1, 'Description is required').max(255),
    category: z.string(),
    vendor: z.string().max(255),
    frequency: z.enum(['weekly', 'monthly', 'quarterly', 'yearly']),
    amount: z.string().refine((v) => v !== '' && Number(v) >= 0, 'Enter a valid amount'),
    currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string(),
    is_active: z.boolean(),
    notes: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.scope === 'employee' && !val.employee) {
      ctx.addIssue({ path: ['employee'], code: z.ZodIssueCode.custom, message: 'Pick an employee' });
    }
    if (val.end_date && val.start_date && val.end_date < val.start_date) {
      ctx.addIssue({ path: ['end_date'], code: z.ZodIssueCode.custom, message: 'End can’t be before start' });
    }
  });

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'employee', 'description', 'category', 'vendor', 'frequency',
  'amount', 'currency', 'start_date', 'end_date', 'is_active', 'notes',
] as const;

interface RecurringExpenseFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (values: RecurringExpenseInput) => Promise<unknown>;
  onCancel: () => void;
}

export function RecurringExpenseForm({
  defaultValues,
  submitLabel,
  onSubmit,
  onCancel,
}: RecurringExpenseFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      scope: 'company',
      employee: '',
      description: '',
      category: 'software',
      vendor: '',
      frequency: 'monthly',
      amount: '',
      currency: 'USD',
      start_date: '',
      end_date: '',
      is_active: true,
      notes: '',
      ...defaultValues,
    },
  });

  const scope = useWatch({ control: form.control, name: 'scope' });

  async function handleSubmit(values: FormValues) {
    const input: RecurringExpenseInput = {
      employee: values.scope === 'employee' && values.employee ? Number(values.employee) : null,
      description: values.description,
      category: values.category,
      vendor: values.vendor,
      frequency: values.frequency,
      amount: values.amount.trim(),
      currency: values.currency,
      start_date: values.start_date,
      end_date: values.end_date || null,
      is_active: values.is_active,
      notes: values.notes,
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-6">
        <FormSection variant="card" title="Scope" description="Who does this cost belong to?">
          <FormField
            control={form.control}
            name="scope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="company">Company-wide</SelectItem>
                    <SelectItem value="employee">Per employee</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {scope === 'employee' ? (
            <FormField
              control={form.control}
              name="employee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Employee
                    <InfoHint text="This cost rolls up under their HRM profile." />
                  </FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="recurring-expense-employee"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchEmployees}
                      resolveLabel={resolveEmployeeLabel}
                      placeholder="Select an employee"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
        </FormSection>

        <FormSection variant="card" title="Cost" description="What and how much.">
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input placeholder="Claude Pro seat" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="vendor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor</FormLabel>
                  <FormControl>
                    <Input placeholder="Anthropic" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_8rem_8rem]">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount (per period)</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} step="0.01" placeholder="20.00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Frequency</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {RECURRING_FREQUENCIES.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Schedule" description="When it runs.">
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Start date
                    <InfoHint text="Charges start from today at the earliest." />
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End date (optional)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Active</FormLabel>
                <FormControl>
                  <div className="border-input flex h-9 items-center justify-between rounded-md border bg-transparent px-3 shadow-xs dark:bg-input/30">
                    <span className="text-muted-foreground text-sm">
                      {field.value ? 'Generating charges' : 'Paused — no charges'}
                    </span>
                    <Switch checked={field.value} onCheckedChange={field.onChange} aria-label="Active" />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea rows={3} placeholder="Anything worth remembering…" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
